import { useEffect, useState } from "react";
import { Search, Check } from "lucide-react";
import { Card, CardContent } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Avatar, AvatarFallback, AvatarImage } from "./ui/avatar";
import { TeamRepository } from "../infrastructure/repositories/TeamRepository";
import { UserConnectionStatus } from "../types/Team";
import { Badge } from "./ui/badge";
import { Swimmer } from "../types";
import { secureStorage } from "@/utils/secureStorage";

export function ChangeSwimmerContent() {
  const [swimmers, setSwimmers] = useState<Swimmer[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState("");

  const firstNonEmpty = (...values: any[]): string => {
    for (const value of values) {
      const text = value == null ? "" : String(value).trim();
      if (text) return text;
    }
    return "";
  };

  const normalizeSwimmer = (s: any): Swimmer => {
    const fullName = firstNonEmpty(
      s?.name,
      s?.full_name,
      s?.fullName,
      s?.user?.name,
      s?.user?.full_name,
      s?.data?.name
    );
    const nameParts = fullName.split(" ").filter(Boolean);
    const nameFromFullFirst = nameParts[0] || "";
    const nameFromFullLast = nameParts.slice(1).join(" ");

    return {
      id: firstNonEmpty(s?.id, s?.user_id, s?.third_party_swimmer_id),
      coach_id: firstNonEmpty(s?.coach_id, s?.CoachId, s?.coachId),
      fname: firstNonEmpty(
        s?.fname,
        s?.first_name,
        s?.firstName,
        s?.user?.fname,
        s?.user?.first_name,
        s?.data?.fname,
        nameFromFullFirst
      ),
      lname: firstNonEmpty(
        s?.lname,
        s?.last_name,
        s?.lastName,
        s?.user?.lname,
        s?.user?.last_name,
        s?.data?.lname,
        nameFromFullLast
      ),
      email: firstNonEmpty(s?.email, s?.user_email, s?.user?.email, s?.data?.email, s?.username),
      image:
        firstNonEmpty(
          s?.image,
          s?.profile_pic,
          s?.profilePicture,
          s?.avatar,
          s?.photo,
          s?.user?.image,
          s?.data?.image
        ) || null,
      dob: firstNonEmpty(s?.dob, s?.data?.dob),
      gender: firstNonEmpty(s?.gender, s?.data?.gender),
      lang: firstNonEmpty(s?.lang, s?.data?.lang),
      height: firstNonEmpty(s?.height, s?.data?.height),
      weight: firstNonEmpty(s?.weight, s?.data?.weight),
      shoesize: firstNonEmpty(s?.shoesize, s?.data?.shoesize),
      handsize: firstNonEmpty(s?.handsize, s?.data?.handsize),
      third_party_swimmer_id: firstNonEmpty(s?.third_party_swimmer_id, s?.data?.third_party_swimmer_id) || null,
    };
  };

  useEffect(() => {
    const fetchSwimmers = async () => {
      const repo = new TeamRepository();
      try {
        const coachId = Number(localStorage.getItem("coach_id")) || 0;
        const status = UserConnectionStatus.Accepted;
        const response = await repo.getCoachSwimmersApi(coachId, status);
 
        const swimmersArray: Swimmer[] = (
          Array.isArray(response.data)
            ? response.data
            : JSON.parse(typeof response.data === "string" ? response.data : "[]")
        ).map((s: any) => normalizeSwimmer(s));

        const userDataRaw = localStorage.getItem("user_data");
        const loggedInUser = userDataRaw ? JSON.parse(userDataRaw) : null;
        const currentUserSwimmerId = String(localStorage.getItem("current_user_swimmer_id") || "");

        let allSwimmers = [...swimmersArray];
        const hasCurrentUser = currentUserSwimmerId
          ? allSwimmers.some((s) => String(s.id) === currentUserSwimmerId)
          : false;

        if (currentUserSwimmerId && !hasCurrentUser) {
          const source = loggedInUser || {};
          const currentUserName = String(secureStorage.get("current_user_name") || "").trim();
          const [storedFName, ...storedLNameParts] = currentUserName.split(" ").filter(Boolean);
          const storedLName = storedLNameParts.join(" ");
          const currentUserSwimmer = normalizeSwimmer({
            ...source,
            id: currentUserSwimmerId,
            coach_id: firstNonEmpty(source?.coach_id, localStorage.getItem("coach_id")),
            fname: firstNonEmpty(source?.fname, source?.first_name, storedFName),
            lname: firstNonEmpty(source?.lname, source?.last_name, storedLName),
            email: firstNonEmpty(source?.email, source?.user_email, secureStorage.get("current_user_email")),
            image: firstNonEmpty(source?.image, source?.profile_pic, secureStorage.get("current_user_pic")),
          });

          if (currentUserSwimmer.id) {
            allSwimmers = [currentUserSwimmer, ...allSwimmers];
          }
        }

        const uniqueSwimmers: Swimmer[] = [];
        const seenIds = new Set<string>();
        const seenEmails = new Set<string>();
        const seenNameDob = new Set<string>();

        for (const swimmer of allSwimmers) {
          const idKey = String(swimmer.id || "").trim();
          const emailKey = String(swimmer.email || "").trim().toLowerCase();
          const nameDobKey = `${String(swimmer.fname || "").trim().toLowerCase()}|${String(
            swimmer.lname || ""
          )
            .trim()
            .toLowerCase()}|${String(swimmer.dob || "").trim()}`;
          const isCurrentUser = !!currentUserSwimmerId && idKey === String(currentUserSwimmerId);

          const isDuplicate =
            (idKey && seenIds.has(idKey)) ||
            (!isCurrentUser && emailKey && seenEmails.has(emailKey)) ||
            (!isCurrentUser && nameDobKey !== "||" && seenNameDob.has(nameDobKey));

          if (isDuplicate) continue;

          uniqueSwimmers.push(swimmer);
          if (idKey) seenIds.add(idKey);
          if (emailKey) seenEmails.add(emailKey);
          if (nameDobKey !== "||") seenNameDob.add(nameDobKey);
        }

        const fallbackPrimary = uniqueSwimmers[0] ?? null;

        if (!fallbackPrimary) {
          setSwimmers([]);
          return;
        }

        const selectedSwimmerId =
          localStorage.getItem("selected_swimmer_id") ||
          currentUserSwimmerId ||
          fallbackPrimary.id;

        const updatedSwimmers: Swimmer[] = uniqueSwimmers.map((s) => ({
          ...s,
          role:
            String(s.id) === String(selectedSwimmerId)
              ? ("Primary" as Swimmer["role"])
              : ("Secondary" as Swimmer["role"]),
        }));

        const selectedSwimmer = updatedSwimmers.find((s) => String(s.id) === String(selectedSwimmerId));
        if (selectedSwimmer) {
           localStorage.setItem("selected_swimmer_id", selectedSwimmer.id);
        
          localStorage.setItem(
            "selected_swimmer_name",
            `${selectedSwimmer.fname} ${selectedSwimmer.lname}`
          );
          localStorage.setItem(
            "selected_swimmer_data",
            JSON.stringify(selectedSwimmer)
          );
          localStorage.setItem(
            "selected_swimmer_pic",
            selectedSwimmer.image ?? ""
          );
        }

        setSwimmers(updatedSwimmers);
      } catch (err) {
        console.error("Error fetching swimmers:", err);
        setError("Failed to load swimmers");
      }
    };

    fetchSwimmers();
  }, []);

  const switchSwimmer = (swimmer: Swimmer) => {
    localStorage.setItem("selected_swimmer_id", swimmer.id);
    localStorage.setItem("selected_swimmer_name", swimmer.fname + " " + swimmer.lname);
    localStorage.setItem("selected_swimmer_data", JSON.stringify(swimmer));
    localStorage.setItem("selected_swimmer_pic", swimmer.image ?? "");
    console.log("coachid", swimmer.coach_id);
    localStorage.setItem("selected_swimmer_coach_id", swimmer.coach_id ?? "");

    // Update UI roles
    setSwimmers((prev) =>
      prev.map((s) => ({
        ...s,
        role:
          s.id === swimmer.id
            ? ("Primary" as Swimmer["role"])
            : ("Secondary" as Swimmer["role"]),
      }))
    );
  };

  if (error) return <p className="text-red-500">{error}</p>;

  const filteredSwimmers = swimmers.filter((s) => {
    const fullName = `${s.fname} ${s.lname}`.toLowerCase();
    return (
      fullName.includes(searchTerm.toLowerCase()) ||
      s.email?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Manage Swimmers</h1>
          <p className="text-muted-foreground">
            Switch between different swimmer profiles
          </p>
        </div>
        {/* <Button>
          <Plus className="mr-2 h-4 w-4" />
          Add Swimmer
        </Button> */}
      </div>

      {/* Search */}
      <Card>
        <CardContent className="pt-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search swimmers by name or email..."
              className="pl-10"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </CardContent>
      </Card>

      {/* Swimmers List */}
      <div className="space-y-4">
        {filteredSwimmers.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center">
            No swimmers found
          </p>
        ) : (
          filteredSwimmers.map((swimmer) => (
            <Card key={swimmer.id} className="hover:shadow-md transition-shadow">
              <CardContent className="p-6 flex justify-between items-center">
                <div className="flex items-center space-x-4">
                  <Avatar className="h-12 w-12">
                    <AvatarImage src={swimmer.image || undefined} />
                    <AvatarFallback>
                      {`${swimmer.fname[0] || ""}${swimmer.lname[0] || ""}`}
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <h3 className="font-semibold">
                      {`${swimmer.fname} ${swimmer.lname}`}
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      {swimmer.email}
                    </p>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  {swimmer.role === "Primary" ? (
                    <Badge variant="outline" className="flex items-center">
                      <Check className="mr-1 h-3 w-3" />
                      Current
                    </Badge>
                  ) : (
                    <Button onClick={() => switchSwimmer(swimmer)}>
                      Switch To
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>
    </div>
  );
}
